import React from "react";
import { motion } from "framer-motion";
import { useParams, useLocation } from 'react-router-dom';
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Toolbar from '@mui/material/Toolbar';
import Button from "@mui/material/Button";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Typography from '@mui/material/Typography';
import CardSection from "../../components/CardSection";
import BannerDynamic from "../../components/BannerDynamic";

import { useApp } from "../../../context";

import PageTemplate from '../../components/PageTemplate';
import { filterByProperty, groupByProperty } from "../../../utils";
import { selectAds, adFromListing } from "../../../utils/advertising";
import { getLocations } from "../../../utils/platformConfig";
import { resolveDestinationSlug } from "../../../data/destinationArtwork";
import { LOCATIONS as CODE_LOCATIONS } from "../../../data/locations";
import { gradientTextSx } from "../../../theme";

import './styles.css';

function generateRandom(maxLimit = 10) {
  return Math.floor(Math.random() * maxLimit);
}

export default function Destination() {
  // const { state: { serviceProviders } } = useContext(Store);
  const { serviceListing, adverts, listingsLoaded } = useApp();

  const location = useLocation();
  const { slug } = useParams();
  const [advert, setAdvert] = React.useState(null);

  /**
   * The canonical destination list.
   *
   * Seeded synchronously from the compiled-in list, exactly as
   * PopularDestinations does, so the slug resolves on the first render and
   * there is no flash of "not found" while `app_config` is read. `locationsLoaded`
   * then distinguishes "still fetching" from "genuinely unknown" — needed only
   * for a destination that exists in config but not in the bundle.
   */
  const [locations, setLocations] = React.useState(CODE_LOCATIONS);
  const [locationsLoaded, setLocationsLoaded] = React.useState(false);

  React.useEffect(() => {
    let live = true;
    getLocations()
      .then((next) => { if (live) setLocations(next); })
      .finally(() => { if (live) setLocationsLoaded(true); });
    return () => { live = false; };
  }, []);

  /**
   * Resolved from the URL, not from router state.
   *
   * This page used to read its subject from `location.state.name`, set by
   * whichever tile linked here. That made every destination URL unshareable
   * and unrefreshable — arriving without state left the title empty, which
   * filtered the listings to nothing and rendered a heading with no text
   * beneath it. The `:slug` in the route was decorative; nothing resolved
   * anything from it.
   *
   * Router state is now only a fallback, for a destination the resolver does
   * not recognise but a caller already knew the name of.
   */
  const resolved = React.useMemo(
    () => resolveDestinationSlug(slug, locations),
    [slug, locations]
  );

  // The canonical VALUE, because listings are filtered on it by exact string
  // equality. Never the label, which an admin may change freely.
  const title = resolved?.value ?? location.state?.name ?? '';
  const heading = resolved?.label || title;

  // A slug nothing matches, once the config read has settled. Distinct from
  // "no listings here yet", which is a real destination with an empty result.
  const notFound = !title && locationsLoaded;

  // Derived rather than captured in a mount-only effect. That effect read
  // `serviceListing` once with an empty dependency array, so opening this page
  // before the first onSnapshot landed left it permanently empty, and listings
  // published afterwards never appeared.
  const data = React.useMemo(
    () => (title ? filterByProperty(serviceListing, "location", title) : []),
    [serviceListing, title]
  );

  const serviceListLocal = React.useMemo(() => groupByProperty(data, "type"), [data]);

  // Re-picked only once the current advert drops out of the list — otherwise
  // an unrelated snapshot would shuffle the banner while it is being read.
  React.useEffect(() => {
    setAdvert((current) => {
      if (current && data.some((listing) => listing.id === current.id)) return current;
      return data.length ? data[generateRandom(data.length)] : null;
    });
  }, [data]);

  const listingsById = React.useMemo(() => {
    const map = {};
    (serviceListing || []).forEach((l) => { map[l.id] = l; });
    return map;
  }, [serviceListing]);

  /**
   * A sold placement for this destination, or the featured listing as house
   * inventory.
   *
   * `location` is passed so an advert bought for Victoria Falls does not
   * appear on the Kariba page — matched by exact string equality against
   * `showOnLocations`, which is why the admin form picks those from the
   * canonical list rather than accepting free text.
   */
  const bannerAd = React.useMemo(() => {
    const sold = selectAds(adverts, 'DESTINATION_BANNER', {
      location: title,
      listingsById,
      listingsLoaded,
    })[0];
    return sold || adFromListing(advert);
  }, [adverts, title, listingsById, listingsLoaded, advert]);

  if (notFound) {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ textAlign: 'center', px: 3, py: 10 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            We don&apos;t have a destination by that name
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            The link may be out of date, or the destination may have been renamed.
          </Typography>
          <Button component={Link} to="/" variant="contained">
            Back to Vhakacha
          </Button>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <Toolbar />
      {/* An explicit flex row. `gradientTextSx` sets `display: inline-block`,
          so the heading already sat beside the button — but as inline content,
          aligned on the text baseline, which puts a 34px heading and a 14px
          button label visibly out of line with each other. */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          my: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          component={Link}
          to="/"
          variant="text"
          startIcon={<ChevronLeftIcon />}
          sx={{ flexShrink: 0 }}
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" sx={{ ...gradientTextSx, minWidth: 0 }}>
          {heading}
        </Typography>
      </Box>

      <motion.div
        initial="initial"
        animate="enter"
        exit="exit"
        variants={{ exit: { transition: { staggerChildren: 0.1 } } }}
      >
        <BannerDynamic ad={bannerAd} />
        {
          serviceListLocal && Object.keys(serviceListLocal).map((key, i) => {
            return <CardSection key={`CardSection-${key}-${i}`} title={key} serviceProviders={serviceListLocal[key]} />
          })
        }
        {/* A real destination with nothing published yet. Distinct from the
            not-found above, and it used to be indistinguishable: both rendered
            an empty page under a heading. */}
        {listingsLoaded && !data.length && (
          <Typography color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>
            Nothing is listed in {heading} just yet.
          </Typography>
        )}
      </motion.div>
    </PageTemplate>
  );
}
